/**
 * CircleNaming — modal for circle name voting.
 * Appears when circle.status === 'naming' (or 'objection').
 *
 * Props:
 *   circle    {object}   circle doc
 *   onVoted   {fn}       called after successful vote
 *   onClose   {fn}       called to close without voting
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "../hooks/use-toast";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CircleNaming({ circle, onVoted, onClose }) {
  const [availableNames, setAvailableNames] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const isNaming = circle?.status === "naming";
  const isObjection = circle?.status === "objection";

  useEffect(() => {
    if (!circle?.circle_id) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/circles/available-names?circle_id=${circle.circle_id}`,
          { withCredentials: true }
        );
        setAvailableNames(data.names || []);
      } catch {
        setAvailableNames([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [circle?.circle_id]);

  function toggleName(name) {
    setSelected((prev) => {
      if (prev.includes(name)) return prev.filter((n) => n !== name);
      if (prev.length >= 3) {
        toast({ title: "Máximo 3 nomes", variant: "destructive" });
        return prev;
      }
      return [...prev, name];
    });
  }

  async function handleVote() {
    if (!selected.length) {
      toast({ title: "Escolha pelo menos 1 nome", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/circles/${circle.circle_id}/vote-name`,
        { acceptable_names: selected },
        { withCredentials: true }
      );
      toast({ title: "Voto registrado!", description: "Obrigada pela sua escolha." });
      onVoted?.();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao votar. Tente novamente.";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-violet-700 px-6 py-5 text-white">
          <div className="text-3xl mb-1">🌿</div>
          {isNaming && (
            <>
              <h2 className="text-xl font-bold">Escolha o nome do círculo</h2>
              <p className="text-purple-200 text-sm mt-1">
                Marque até 3 nomes que você aceitaria. O nome com mais votos será escolhido.
              </p>
            </>
          )}
          {isObjection && (
            <>
              <h2 className="text-xl font-bold">Confirmar nome do círculo</h2>
              <p className="text-purple-200 text-sm mt-1">
                A votação indicou o nome abaixo. Alguém tem objeção?
              </p>
            </>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {isObjection ? (
            <NameChosenView circle={circle} onClose={onClose} />
          ) : (
            <NamingView
              names={availableNames}
              selected={selected}
              onToggle={toggleName}
              loading={loading}
              onVote={handleVote}
              submitting={submitting}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function NamingView({ names, selected, onToggle, loading, onVote, submitting }) {
  if (loading) {
    return (
      <div className="space-y-2 pb-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-10 rounded-lg bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <>
      <p className="text-xs text-gray-500 mb-3">
        {selected.length}/3 nomes selecionados
      </p>
      <div className="space-y-2 max-h-64 overflow-y-auto mb-5">
        {names.map((name) => {
          const isSelected = selected.includes(name);
          return (
            <button
              key={name}
              onClick={() => onToggle(name)}
              className={`w-full text-left px-4 py-2.5 rounded-xl border-2 font-medium transition-all text-sm ${
                isSelected
                  ? "border-purple-500 bg-purple-50 text-purple-700"
                  : "border-gray-200 hover:border-purple-300 text-gray-700"
              }`}
            >
              {isSelected && <span className="mr-2">✓</span>}
              {name}
            </button>
          );
        })}
      </div>
      <Button
        onClick={onVote}
        disabled={submitting || !selected.length}
        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
      >
        {submitting ? "Enviando..." : "Votar"}
      </Button>
    </>
  );
}

function NameChosenView({ circle, onClose }) {
  return (
    <div className="text-center">
      <div className="inline-block bg-purple-100 rounded-2xl px-6 py-4 mb-5">
        <p className="text-purple-600 text-sm font-medium mb-1">Nome do círculo</p>
        <p className="text-2xl font-bold text-purple-800">{circle.name}</p>
      </div>
      <p className="text-gray-600 text-sm mb-6">
        O nome do seu círculo foi escolhido por votação!
      </p>
      <Button
        onClick={onClose}
        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
      >
        Fechar
      </Button>
    </div>
  );
}
